import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { customersAPI, exportsAPI } from '../services/api';
import { personName } from '../utils/person';
import { getSellerProductsPath } from '../utils/seller';
import PersonAvatar from './PersonAvatar';
import Pagination from './Pagination';
import ExportCsvButton from './ExportCsvButton';
import './People.css';

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, per_page: 15, total: 0 });
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    loadCustomers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCustomers = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: pagination.per_page,
      };
      if (search) params.search = search;
      if (filter === 'suspended') params.is_suspended = true;

      const response = await customersAPI.getAll(params);
      setCustomers(response.data.data || []);
      setPagination(response.data.pagination || pagination);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadCustomers(1);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setTimeout(() => loadCustomers(1), 100);
  };

  const handleSuspend = async (id) => {
    if (!window.confirm('Suspend this customer?')) return;
    try {
      await customersAPI.suspend(id);
      loadCustomers(pagination.page);
    } catch (error) {
      toast.error('Error suspending customer: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleUnsuspend = async (id) => {
    try {
      await customersAPI.unsuspend(id);
      loadCustomers(pagination.page);
    } catch (error) {
      toast.error('Error unsuspending customer: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this customer permanently?')) return;
    try {
      await customersAPI.delete(id);
      loadCustomers(pagination.page);
    } catch (error) {
      toast.error('Error deleting customer: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (id) => {
    navigate(`/customers/${id}/edit`);
  };

  if (loading && customers.length === 0) {
    return <div className="loading">Loading customers…</div>;
  }

  const totalLabel =
    pagination.total > 0
      ? `${pagination.total} customer${pagination.total === 1 ? '' : 's'}`
      : 'Buyers registered in the app';

  return (
    <div className="people-page">
      <div className="page-toolbar">
        <p className="page-meta">{loading ? 'Loading…' : totalLabel}</p>
        <ExportCsvButton
          exportRequest={exportsAPI.customers}
          filters={{
            search: search || undefined,
            is_suspended: filter === 'suspended' ? true : undefined,
          }}
          filename="customers.csv"
        />
      </div>

      <div className="search-filter-bar">
        <div className="filter-field filter-field--grow">
          <label htmlFor="customers-search">Search</label>
          <input
            id="customers-search"
            type="search"
            placeholder="Name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <div className="filter-field">
          <label htmlFor="customers-filter">Filter</label>
          <select id="customers-filter" value={filter} onChange={handleFilterChange}>
            <option value="">All customers</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        <button type="button" className="btn btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>

      {loading && customers.length > 0 ? (
        <div className="list-loading-hint">Updating…</div>
      ) : null}

      {customers.length === 0 ? (
        <div className="people-empty">No customers match your filters.</div>
      ) : (
        <div className="people-list">
          {customers.map((customer) => {
            const isSuspended = customer.suspended_at !== null;
            const name = personName(customer);

            return (
              <article key={customer.id} className="person-card">
                <PersonAvatar person={customer} size="md" />

                <div className="person-card-main">
                  <h3 className="person-card-name">{name}</h3>
                  <p className="person-card-email">{customer.email}</p>
                  <div className="person-card-meta">
                    {isSuspended ? (
                      <span className="status-badge status-suspended">Suspended</span>
                    ) : (
                      <span className="status-badge status-active">Active</span>
                    )}
                  </div>
                  <div className="person-card-stats">
                    <span>
                      <strong>{customer.orders_count || 0}</strong> orders
                    </span>
                    <button
                      type="button"
                      className="person-stat-link"
                      onClick={() =>
                        navigate(getSellerProductsPath(customer.id, name))
                      }
                    >
                      <strong>{customer.products_count ?? 0}</strong> products
                    </button>
                    <span>
                      <strong>{customer.wishlist_count || 0}</strong> wishlist
                    </span>
                    <span>
                      <strong>
                        AED {(customer.auction_deposit_balance ?? 0).toLocaleString()}
                      </strong>{' '}
                      deposit
                    </span>
                  </div>
                </div>

                <div className="person-card-actions">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => handleEdit(customer.id)}
                  >
                    Edit
                  </button>
                  {isSuspended ? (
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={() => handleUnsuspend(customer.id)}
                    >
                      Unsuspend
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => handleSuspend(customer.id)}
                    >
                      Suspend
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(customer.id)}
                  >
                    Delete
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {pagination.total > pagination.per_page && (
        <Pagination pagination={pagination} onPageChange={loadCustomers} />
      )}
    </div>
  );
};

export default Customers;
